import { ParsedUrlQuery } from 'querystring'
import { createFilterOptions } from '@mui/material/useAutocomplete'
import { Filter } from 'components/core/search-filter/filter'
import { getFilterKeyToLabelMap } from 'components/core/task/ContextualTaskSearchFilter'
import { PendingActionFilter as PendingActionFilterEnum } from 'pbts/c1/api/task/v1/search'
import { useCallback, useMemo } from 'react'
import { IntlShape, useIntl } from 'react-intl'

export const PendingActionQueryParam = 'pendingAction'

type PendingActionFilterProps = {
    selected: PendingActionFilterEnum | undefined
    onChange: (values: PendingActionFilterEnum[] | undefined, loading?: boolean) => void
}

const getOptionSelected = (option1: PendingActionFilterEnum, option2: PendingActionFilterEnum): boolean => {
    return option1 === option2
}

const pendingActionFilterToIntl = (filter: PendingActionFilterEnum, intl: IntlShape): string => {
    switch (filter) {
        case PendingActionFilterEnum.WITH_PENDING:
            return intl.formatMessage({
                id: 'pending_action_filter_with_pending',
                defaultMessage: 'With decision',
            })
        case PendingActionFilterEnum.WITHOUT_PENDING:
            return intl.formatMessage({
                id: 'pending_action_filter_without_pending',
                defaultMessage: 'Without decision',
            })
        default:
            return intl.formatMessage({
                id: 'pending_action_filter_all',
                defaultMessage: 'All',
            })
    }
}

const pendingActionFilterToString = (filter: PendingActionFilterEnum): string => {
    switch (filter) {
        case PendingActionFilterEnum.WITH_PENDING:
            return 'WITH_PENDING'
        case PendingActionFilterEnum.WITHOUT_PENDING:
            return 'WITHOUT_PENDING'
        default:
            return 'UNSPECIFIED'
    }
}

const stringToPendingActionFilter = (str: string): PendingActionFilterEnum | undefined => {
    switch (str) {
        case 'WITH_PENDING':
            return PendingActionFilterEnum.WITH_PENDING
        case 'WITHOUT_PENDING':
            return PendingActionFilterEnum.WITHOUT_PENDING
        default:
            return undefined
    }
}

export const PendingActionFilter = ({ selected, onChange }: PendingActionFilterProps): React.ReactElement => {
    const intl = useIntl()
    const filterOptions = createFilterOptions<PendingActionFilterEnum>({})
    const filterKeyToLabel = getFilterKeyToLabelMap(intl)

    const pendingActionOptions: PendingActionFilterEnum[] = useMemo(() => {
        return [
            PendingActionFilterEnum.WITH_PENDING,
            PendingActionFilterEnum.WITHOUT_PENDING,
        ]
    }, [])

    const renderLabel = useCallback((option: PendingActionFilterEnum): string => pendingActionFilterToIntl(option, intl), [intl])

    // Load initial filter from query.
    const fromQuery = useCallback((query: ParsedUrlQuery): PendingActionFilterEnum[] => {
        const filterValue = query[PendingActionQueryParam]
        if (!filterValue || Array.isArray(filterValue)) {
            return []
        }

        const parsed = stringToPendingActionFilter(filterValue)
        if (parsed === undefined) {
            return []
        }

        return [parsed]
    }, [])

    // Serialize selection to query.
    const toQuery = useCallback((query: ParsedUrlQuery, filters: PendingActionFilterEnum[]): void => {
        if (filters.length > 0) {
            const filterStr = pendingActionFilterToString(filters[0])
            if (filterStr !== 'UNSPECIFIED') {
                query[PendingActionQueryParam] = filterStr
            } else {
                delete query[PendingActionQueryParam]
            }
        } else {
            delete query[PendingActionQueryParam]
        }
    }, [])

    // Convert single selected value to array for Filter component
    const selectedArray = useMemo(() => {
        if (selected === undefined || selected === PendingActionFilterEnum.UNSPECIFIED) {
            return []
        }
        return [selected]
    }, [selected])

    // Wrap onChange to handle the Filter component's array-based callback
    const handleChange = useCallback((values: PendingActionFilterEnum[], loading: boolean): void => {
        // Only keep the last selected value (single selection behavior)
        if (values.length > 1) {
            // If multiple selected, keep only the most recently added one
            const newValue = values[values.length - 1]
            onChange([newValue], loading)
        } else {
            onChange(values.length > 0 ? values : undefined, loading)
        }
    }, [onChange])

    return <Filter<PendingActionFilterEnum>
        label={filterKeyToLabel['pending-action'] || intl.formatMessage({
            id: 'filter_label_pending_action',
            defaultMessage: 'Pending review',
        })}
        options={pendingActionOptions}
        selected={selectedArray}
        fromQuery={fromQuery}
        toQuery={toQuery}
        onChange={handleChange}
        filterOptions={filterOptions}
        getOptionSelected={getOptionSelected}
        renderLabel={renderLabel}
        showClearButton
    />
}
